import { useState } from 'react'

import type { ShadowTurn } from '@/api/types'
import { Badge } from '@/ui/primitives'

interface Props {
  turns: ShadowTurn[]
  convId: number
  contextJson: string | null
}

function formatTs(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatLatency(ms: number | null | undefined): string {
  if (ms == null) return ''
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60000).toFixed(1)}m`
}

function catLabel(cat: string | null): string {
  if (!cat) return ''
  const m: Record<string, string> = {
    beauty: 'Beauty', order_status: 'Order', complaint: 'Complaint',
    pricing: 'Pricing', product_availability: 'Avail.', store_info: 'Store',
    how_to_use: 'How-to', social_media: 'Social', general: 'General',
  }
  return m[cat] || cat
}

function catBadge(cat: string | null): 'default' | 'success' | 'warning' | 'danger' {
  if (cat === 'complaint') return 'danger'
  if (cat === 'order_status') return 'warning'
  if (cat === 'beauty') return 'success'
  return 'default'
}

export function TurnComparisonTable({ turns, convId, contextJson }: Props) {
  const [lightboxImg, setLightboxImg] = useState<string | null>(null)
  const [showMarkdown1, setShowMarkdown1] = useState(false)
  const [showMarkdown2, setShowMarkdown2] = useState(false)

  const contextAll: { sender: string; type: string; body: string; ts: string; msg_id?: number }[] = (() => {
    try { return contextJson ? JSON.parse(contextJson) : [] }
    catch { return [] }
  })()

  const agentBlocks: (string | null)[] = turns.map((turn, i) => {
    const nextTurn = turns[i + 1]
    const after = turn.customer_message_ts
    const before = nextTurn?.customer_message_ts || null
    const msgs = contextAll.filter(m =>
      m.type !== 'customer' &&
      m.ts && after &&
      new Date(m.ts) > new Date(after) &&
      (!before || new Date(m.ts) < new Date(before))
    )
    if (msgs.length === 0) return turn.human_agent_response || null
    return msgs.map(m => {
      const time = m.ts ? formatTs(m.ts) : '—'
      return `[${time}] ${m.sender}: ${m.body}`
    }).join('\n\n')
  })

  if (turns.length === 0) {
    return <div className="text-center text-mute text-xs py-8">No turns in this conversation.</div>
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-haze/40">
              <th className="px-3 py-2 text-left text-[10px] text-mute uppercase tracking-wider w-[24%]">Customer</th>
              <th className="px-3 py-2 text-left text-[10px] text-mute uppercase tracking-wider w-[24%]">Human Agent</th>
              <th className="px-3 py-2 text-left text-[10px] text-mute uppercase tracking-wider w-[26%]">Shadow 1</th>
              <th className="px-3 py-2 text-left text-[10px] text-mute uppercase tracking-wider w-[26%]">Shadow 2</th>
            </tr>
          </thead>
          <tbody>
            {turns.map((turn, rIdx) => {
              const turnAtt = (() => {
                try { return turn.attachment_json ? JSON.parse(turn.attachment_json) : null }
                catch { return null }
              })()
              const imgPaths: string[] = (() => {
                if (!turnAtt) return []
                if (turnAtt.type === 'images' && Array.isArray(turnAtt.items)) {
                  return turnAtt.items.filter((i: { local_path?: string }) => i.local_path)
                    .map((_: { local_path?: string }, idx: number) => {
                      const suffix = idx > 0 ? `_${idx}` : ''
                      return `/v1/admin/shadow/images/${convId}/${turn.remote_msg_id}${suffix}`
                    })
                }
                if (turnAtt.local_path) return [`/v1/admin/shadow/images/${convId}/${turn.remote_msg_id}`]
                return []
              })()
              const isAttachmentOnly = !turn.customer_message

              return (
                <tr key={turn.id} className={`align-top ${rIdx % 2 === 0 ? 'bg-ink/15' : ''}`}>
                  <td className="px-3 py-2.5 border-r border-haze/15">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[10px] text-mute/50 font-mono">{formatTs(turn.customer_message_ts)}</span>
                      {turn.category && <Badge variant={catBadge(turn.category)}>{catLabel(turn.category)}</Badge>}
                    </div>
                    <div className="bg-ink-raised border border-haze/40 px-3 py-2">
                      {imgPaths.length > 0 ? (
                        <div className={`flex flex-wrap gap-1.5 ${isAttachmentOnly ? '' : 'mb-1.5'}`}>
                          {imgPaths.map((path, idx) => (
                            <img
                              key={idx}
                              src={path}
                              alt={`Attachment ${idx + 1}`}
                              className="max-w-[45%] max-h-32 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => setLightboxImg(path)}
                            />
                          ))}
                        </div>
                      ) : turn.customer_message ? null : (
                        <div className="text-xs italic text-cream/30">[Image]</div>
                      )}
                      {turn.customer_message ? (
                        <div className="text-xs text-cream whitespace-pre-wrap leading-relaxed break-words">
                          {turn.customer_message}
                        </div>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 border-r border-haze/15">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[10px] font-medium text-ember/70">{turn.human_agent_name || 'Agent'}</span>
                    </div>
                    {agentBlocks[rIdx] ? (
                      <div className="bg-ember/[0.04] border border-ember/8 px-3 py-2">
                        <div className="text-xs text-cream/80 whitespace-pre-wrap leading-relaxed break-words">
                          {agentBlocks[rIdx]}
                        </div>
                      </div>
                    ) : (
                      <div className="text-[10px] text-mute/30 italic py-2">No human response</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {turn.status === 'pending' ? (
                      <div className="text-[10px] text-mute/30 italic py-2 text-center">Pending</div>
                    ) : turn.status === 'failed' ? (
                      <div className="text-[10px] text-ember/50 italic py-2">Failed: {turn.error_message || 'unknown'}</div>
                    ) : turn.ai_response ? (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[9px] font-medium text-emerald/60 uppercase tracking-wider">S1</span>
                          {turn.cost_usd != null && (
                            <span className="text-[9px] text-emerald/60">${turn.cost_usd.toFixed(4)}</span>
                          )}
                          {turn.latency_ms != null && (
                            <span className="text-[9px] text-mute/40">{formatLatency(turn.latency_ms)}</span>
                          )}
                          {turn.ai_response_plain && (
                            <button
                              onClick={() => setShowMarkdown1(!showMarkdown1)}
                              className="text-[9px] px-1 py-0.5 border border-emerald/15 text-emerald/40 hover:text-emerald/70 hover:border-emerald/30 transition-colors ml-auto"
                            >{showMarkdown1 ? 'Plain' : 'MD'}</button>
                          )}
                        </div>
                        <div className="text-xs text-cream/80 whitespace-pre-wrap leading-relaxed">
                          {showMarkdown1 ? turn.ai_response : (turn.ai_response_plain || turn.ai_response)}
                        </div>
                        {(() => {
                          try {
                            const calls = turn.tool_calls_json ? JSON.parse(turn.tool_calls_json) : []
                            if (calls.length === 0) return null
                            return (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {calls.map((tc: { name: string }, i: number) => (
                                  <span key={i} className="inline-flex items-center px-1 py-px text-[9px] bg-ink border border-emerald/15 text-emerald/50">
                                    {tc.name}
                                  </span>
                                ))}
                              </div>
                            )
                          } catch { return null }
                        })()}
                      </div>
                    ) : (
                      <div className="text-[10px] text-mute/30 italic py-2 text-center">—</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {turn.status_2 === 'pending' ? (
                      <div className="text-[10px] text-mute/30 italic py-2 text-center">Pending</div>
                    ) : turn.status_2 === 'failed' ? (
                      <div className="text-[10px] text-ember/50 italic py-2">Failed: {turn.error_message_2 || 'unknown'}</div>
                    ) : turn.ai_response_2 ? (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[9px] font-medium text-amber/60 uppercase tracking-wider">S2</span>
                          {turn.cost_usd_2 != null && (
                            <span className="text-[9px] text-amber/60">${turn.cost_usd_2.toFixed(4)}</span>
                          )}
                          {turn.latency_ms_2 != null && (
                            <span className="text-[9px] text-mute/40">{formatLatency(turn.latency_ms_2)}</span>
                          )}
                          {turn.ai_response_plain_2 && (
                            <button
                              onClick={() => setShowMarkdown2(!showMarkdown2)}
                              className="text-[9px] px-1 py-0.5 border border-amber/15 text-amber/40 hover:text-amber/70 hover:border-amber/30 transition-colors ml-auto"
                            >{showMarkdown2 ? 'Plain' : 'MD'}</button>
                          )}
                        </div>
                        <div className="text-xs text-cream/80 whitespace-pre-wrap leading-relaxed">
                          {showMarkdown2 ? turn.ai_response_2 : (turn.ai_response_plain_2 || turn.ai_response_2)}
                        </div>
                        {(() => {
                          try {
                            const calls = turn.tool_calls_json_2 ? JSON.parse(turn.tool_calls_json_2) : []
                            if (calls.length === 0) return null
                            return (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {calls.map((tc: { name: string }, i: number) => (
                                  <span key={i} className="inline-flex items-center px-1 py-px text-[9px] bg-ink border border-amber/15 text-amber/50">
                                    {tc.name}
                                  </span>
                                ))}
                              </div>
                            )
                          } catch { return null }
                        })()}
                      </div>
                    ) : (
                      <div className="text-[10px] text-mute/30 italic py-2 text-center">—</div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {lightboxImg && (
        <div className="fixed inset-0 z-50 bg-ink/90 flex items-center justify-center" onClick={() => setLightboxImg(null)}>
          <button className="absolute top-4 right-4 text-cream text-2xl hover:text-ember transition-colors" onClick={() => setLightboxImg(null)}>✕</button>
          <img src={lightboxImg} alt="Attachment" className="max-w-[90vw] max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  )
}
